import { FC, FunctionComponent, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { mixed, object } from "yup";
import { TextField } from "../../../../../components/forms/text";
import { validationFields } from "../../../../common/models/fields";
import { FieldConfigHook, FieldHook, RenderFuncProps } from "../common";

const Elem: FC<RenderFuncProps> = ({ item }) => {
  const firstName = validationFields.firstName.useName();
  const middleName = validationFields.middleName.useName();
  const lastName = validationFields.lastName.useName();
  const { control } = useFormContext();

  return (
    <>
      <div>{firstName}</div>
      <Controller
        control={control}
        name={`${item.field}.first`}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            error={!!error}
            helperText={error?.message}
            fullWidth
            type="text"
          />
        )}
      />
      <div>{middleName}</div>
      <Controller
        control={control}
        name={`${item.field}.middle`}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            error={!!error}
            helperText={error?.message}
            fullWidth
            type="text"
          />
        )}
      />
      <div>{lastName}</div>
      <Controller
        control={control}
        name={`${item.field}.last`}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            error={!!error}
            helperText={error?.message}
            fullWidth
            type="text"
          />
        )}
      />
    </>
  );
};

const useSchema = () => {
  const { t } = useTranslation("fields");

  const firstSchema = validationFields.firstName.useSchema();
  const middleSchema = validationFields.middleName.useSchema();
  const lastSchema = validationFields.lastName.useSchema();

  return useMemo(
    () =>
      object()
        .shape({
          first: firstSchema,
          middle: middleSchema,
          last: lastSchema,
        })
        .required(
          t("required-field", {
            ns: "errors",
            field: t("names"),
          })
        )
        .label("names"),
    [firstSchema, middleSchema, lastSchema, t]
  );
};

export const useNamesField: FieldHook = () => {
  const schema = useSchema();
  return useMemo(
    () => ({
      schema,
      elem: Elem,
    }),
    [schema]
  );
};

const ConfigElem: FunctionComponent<{}> = () => {
  const { t } = useTranslation("fields");
  return (
    <>
      <div>{t("name")}</div>
    </>
  );
};

export const useNamesFieldConfig: FieldConfigHook = () => {
  const schema = () => mixed();
  return useMemo(
    () => ({
      schema,
      elem: ConfigElem,
    }),
    [schema]
  );
};
